import React from "react"
import { useTranslation } from "react-i18next"
import * as UtilsApi from "features/api/UtilsApi"

import FormElement from "components/Form/FormElement"
import { countriesList } from "utils/constants"
import { isNotEmpty } from "utils/validation"
import { jsonHash } from "utils/hash"

const POSTALCODE_LENGTH = 5
const FRENCH_CODE = ["FR", "GF", "PF", "RE", "GP", "MQ", "YT", "PM", "WF"]

export const AddressForm = ({
    partner,
    setUpdatedPartner,
    errors,
    setErrors,
    isEdit,
    isViewMode,
    editCallback,
    showLocalErrors,
    setShowLocalErrors
}) => {
    const [formFields, setFormFields] = React.useState({})
    const defaultFormEvents = React.useMemo(() => ({
        ready: true,
        // when form is in edition mode, by default the editable is set at false
        // when the form is not in edition mode, it means creation mode, the form should be editable
        editable: !isEdit,
        viewMode: isViewMode || false
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [isEdit])
    const [formEvents, setFormEvents] = React.useState(defaultFormEvents)
    const formHasChanged = jsonHash(partner) !== jsonHash(formFields)

    React.useEffect(() => {
        // When partner reloaded (after await fetchData()), set data in formFields
        if(partner) setFormFields(partner)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [partner])

    React.useEffect(() => {
        // When we create or update a partner, we have to pass data to parent directly.
        // We don't have any submit button on this component in this case
        setUpdatedPartner((prevFormFields) => ({
            ...prevFormFields,
            numero_et_voie: formFields.numero_et_voie,
            code_postal: formFields.code_postal,
            ville: formFields.ville,
            latitude: formFields.latitude,
            longitude: formFields.longitude,
            pays: formFields.pays,
            telephone_2: formFields.telephone_2,
            code_insee: formFields.code_insee
        }))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formFields])

    const { t } = useTranslation()
    const isFrenchCountrySelected = FRENCH_CODE.includes(formFields?.pays)

    // Automatically fill the latitude / longitude / code_Insee
    React.useEffect(() => {
        if (
            isNotEmpty(formFields.numero_et_voie) && formFields.numero_et_voie.length > POSTALCODE_LENGTH
            && isNotEmpty(formFields.code_postal) && formFields.code_postal.length === POSTALCODE_LENGTH
            && isNotEmpty(formFields.ville)
            && formEvents.editable
        ) {
            ; (async () => {
                const coordinates = await UtilsApi.getCoordinates(formFields.numero_et_voie, formFields.code_postal, formFields.ville)
                if(coordinates) {
                    setFormFields(s => ({
                        ...s,
                        longitude: coordinates?.longitude,
                        latitude: coordinates?.latitude,
                    }))
                }
            })()
        }
    }, [formFields.numero_et_voie, formFields.code_postal, formFields.ville]) // eslint-disable-line


    const handleInputChange = React.useCallback(
        (event) => {
            const target = event.target
            const value = target.type === "checkbox" ? target.checked : target.value
            const name = target.name
            
            setFormFields(s => ({
                ...s,
                [name]: value
            }))
        }, []
    )

    const handleValidation = React.useCallback((name, errs) => {
        setErrors((s) => {
            const cleanErrors = [...s.filter((e) => e.origin !== name)]
            return [
                ...cleanErrors,
                ...errs
            ]
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const toggleEditableForm = (event) => {
        event.preventDefault()        
        setFormEvents({
            ...formEvents,
            editable: !formEvents.editable
        })
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        // Validate inputs
        const formReady = !(errors.length > 0)

        if (!formReady) {
            setShowLocalErrors(true)
        }
        else {
            // hide error
            setShowLocalErrors(false)
            // prevent form to be submitted again
            setFormEvents(s => ({
                ...s,
                ready: false
            }))

            const updatedPartner = {
                ...formFields,
                numero_et_voie: formFields.numero_et_voie,
                code_postal: formFields.code_postal,
                ville: formFields.ville,
                latitude: formFields.latitude,
                longitude: formFields.longitude,
                pays: formFields.pays,
                telephone_2: formFields.telephone_2,
                code_insee: formFields.code_insee
            }

            await editCallback(updatedPartner)

            setFormEvents(s => ({
                ...s,
                ready: true,
                editable: false
            }))
        }
    }

    const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleInputChange,
        readonly: !formEvents.editable
	}

    return (
        <form className="">
            <section className="c-card p-5 md:p-12 mb-7">
                <h2 className="c-h2 font-semibold mb-7 uppercase">{t("store.contact_details", { ns: "partner" })}</h2>
                <div className="l-grid u-mg-negative-hz-m">
                    <div className="lg:w-1/2 w-full p-5">
                        {/* pays */}
                        <FormElement
                            value={formFields.pays}
                            defaultValue={{
                                label: countriesList.find(b => b.value === formFields.pays)?.label || "",
                                value: formFields.pays
                            }}
                            options={countriesList}
                            name="pays"
                            id="pays"
                            type="select"
                            label={t("store.country", { ns: "partner" })}
                            className="mb-5"
                            required={true}
                            {...formElementCommonProps}
                        />
                    </div>

                    {isNotEmpty(formFields.pays) &&
                        <>
                            <div className="lg:w-1/2 w-full p-5">
                                {/* address */}
                                <FormElement
                                    value={formFields.numero_et_voie}
                                    name="numero_et_voie"
                                    type="text"
                                    label={t("store.address", { ns: "partner" })}
                                    className="mb-5"
                                    required={true}
                                    {...formElementCommonProps}
                                />
                            </div>
                            <div className="lg:w-1/2 w-full p-5">
                                {/* postCode */}
                                <FormElement
                                    value={formFields.code_postal}
                                    name="code_postal"
                                    type="text"
                                    label={t("store.postcode", { ns: "partner" })}
                                    className="mb-5"
                                    required={true}
                                    {...formElementCommonProps}
                                />
                            </div>
                            <div className="lg:w-1/2 w-full p-5">
                                {/* city */}
                                {/* {isFrenchCountrySelected &&
                                    <FormElement
                                        value={formFields.ville}
                                        name="ville"
                                        type="city"
                                        label={t("store.city", { ns: "partner" })}
                                        className="mb-5"
                                        required={true}
                                        postCode={formFields.code_postal}
                                        updateInseeCode={setFormFields}
                                        {...formElementCommonProps}
                                    />
                                }
                                {!isFrenchCountrySelected && */}
                                    <FormElement
                                        value={formFields.ville}
                                        name="ville"
                                        type={isFrenchCountrySelected ? "city" : "text"}
                                        label={t("store.city", { ns: "partner" })}
                                        className="mb-5"
                                        required={true}
                                        {...formElementCommonProps}
                                        {...isFrenchCountrySelected && {
                                            postCode: formFields.code_postal,
                                            updateInseeCode: setFormFields
                                        }}
                                    />
                         
                            </div>

                            <div className="lg:w-1/2 w-full p-5">
                                {/* latitude */}
                                <FormElement
                                    value={formFields.latitude}
                                    name="latitude"
                                    type="text"
                                    label={t("store.latitude", { ns: "partner" })}
                                    className="mb-5"
                                    required={false}
                                    {...formElementCommonProps}
                                />
                            </div>

                            <div className="lg:w-1/2 w-full p-5">
                                {/* longitude */}
                                <FormElement
                                    value={formFields.longitude}
                                    name="longitude"
                                    type="text"
                                    label={t("store.longitude", { ns: "partner" })}
                                    className="mb-5"
                                    required={false}
                                    {...formElementCommonProps}
                                />
                            </div>

                            <div className="lg:w-1/2 w-full p-5">
                                {/* phoneNumber */}
                                <FormElement
                                    value={formFields.telephone_2}
                                    name="telephone_2"
                                    type="phone"
                                    label={t("store.phonenumber", { ns: "partner" })}
                                    className="mb-5"
                                    required={true}
                                    defaultCountry={formFields.pays}
                                    disabled={!formEvents.editable}
                                    {...formElementCommonProps}
                                />
                            </div>
                        </>
                    }
                </div>
            
                {isEdit && !isViewMode &&
                    <div className="flex justify-end">
                        {formEvents.editable
                            ?
                                <>
                                    <button
                                        type="button"
                                        className="c-btn c-btn--white-bordered c-btn--m px-12 mx-5 mb-7"
                                        onClick={toggleEditableForm}
                                    >
                                        {t("global.cancel")}
                                    </button>
                                    <button
                                        type="submit"
                                        className="c-btn c-btn--primary c-btn--m px-12 mx-5 mb-7"
                                        onClick={handleSubmit}
                                        disabled={!formEvents.ready || !formHasChanged} 
                                    >
                                        {t("global.save")}
                                    </button>
                                </>
                            :
                                <button
                                    type="button"
                                    className="c-btn c-btn--primary px-12 mx-5"
                                    onClick={toggleEditableForm}
                                >
                                    {t("global.update")}
                                </button>
                        }
                    </div>
                }
            </section>
        </form>                    

    )
}
